import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowLeft, ChevronRight, Crown } from "lucide-react";
import { WalletTransactions } from "@/components/wallet-transactions";
import { useBoideUser } from "@/hooks/use-boide-user";
import { useQuotaStatus } from "@/hooks/use-quota-status";
import { planLabelOf } from "@/components/subscription/quota-row";

export const Route = createFileRoute("/_authenticated/bien-dong-so-du")({
  head: () => ({
    meta: [
      { title: "Biến Động Số Dư - Lịch Sử Giao Dịch & Nạp Tiền" },
      {
        name: "description",
        content:
          "Theo dõi lịch sử nạp tiền, giao dịch và biến động số dư trong tài khoản Bói Đê. Xem ngay!",
      },
    ],
  }),
  component: BienDongSoDuPage,
});

function formatMoney(amount: number): string {
  return `${amount.toLocaleString("vi-VN")}đ`;
}

function BienDongSoDuPage() {
  const { user } = useBoideUser();
  const balance = user?.amount ?? user?.credits ?? 0;
  const quota = useQuotaStatus();
  const quotaPlan = planLabelOf(quota.data?.subscriptions ?? []);

  return (
    <main className="app-feature-page flex-1 overflow-y-auto px-4 py-6 pb-24 md:px-6 md:py-8 md:pb-8 lg:px-12">
      <div className="mx-auto flex max-w-5xl flex-col gap-6">
        <Link
          to="/thanh-toan"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" /> Về trang Nạp tiền
        </Link>

        <section className="rounded-3xl border border-gold-soft/40 bg-card/70 p-6 shadow-mystic backdrop-blur-xl md:p-8">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-primary/80">Ví Bói Đê</p>
              <h1 className="mt-2 font-display text-2xl text-foreground md:text-3xl">
                Biến động số dư
              </h1>
              <p className="mt-2 text-sm text-muted-foreground">
                Theo dõi nạp tiền, chi tiêu chat và số dư sau mỗi giao dịch.
              </p>
            </div>
            <div className="rounded-2xl border border-gold-soft/50 bg-background/40 p-4 text-right">
              <p className="text-xs uppercase tracking-widest text-muted-foreground">
                Số dư hiện tại
              </p>
              <p className="mt-1 font-display text-2xl text-primary">{formatMoney(balance)}</p>
              {/* Quota gói ngay dưới số dư: chi tiêu trừ HẠN MỨC GÓI trước, hết mới
                  tới ví — nên trang biến động số dư sẽ "im" trong lúc còn quota, và
                  không nói ra thì người dùng tưởng giao dịch chưa được ghi. */}
              {quota.data ? (
                <Link
                  to="/goi"
                  className="group mt-2 flex items-center justify-end gap-1 text-[11px] text-muted-foreground transition hover:text-primary"
                >
                  <Crown className="size-3 shrink-0 text-primary" />
                  {quotaPlan.primary ? (
                    <span>
                      {/* Tên gói đã chứa chữ "Gói" — xem ghi chú ở `QuotaRow`. */}
                      <strong className="font-semibold text-foreground">
                        {quotaPlan.primary.plan_name}
                      </strong>
                      {quotaPlan.extra > 0 ? (
                        <span className="text-primary"> +{quotaPlan.extra}</span>
                      ) : null}{" "}
                      · còn{" "}
                      <strong className="font-semibold tabular-nums text-primary">
                        {formatMoney(quota.data.remaining)}
                      </strong>{" "}
                      hôm nay
                    </span>
                  ) : (
                    <span>Chưa có gói quota ngày</span>
                  )}
                  <ChevronRight className="size-3 shrink-0 transition group-hover:translate-x-0.5" />
                </Link>
              ) : null}
            </div>
          </div>
        </section>

        <WalletTransactions />
      </div>
    </main>
  );
}
